/**
 * Photos: shrink on the phone, keep the file locally, upload when there's
 * signal. The record that uses a photo stores its final storage path right
 * away, so the record can sync (and be read by a manager) before the photo
 * itself has arrived.
 */
import { getDb, newId, storagePath, type PendingUpload } from '@routeback/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { supabase } from './supabase';

export const MAX_EDGE = 1600;
export const JPEG_QUALITY = 0.7;

/** Longest edge capped at MAX_EDGE, re-encoded as JPEG. Falls back to the original file. */
export async function shrinkImage(file: Blob): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * scale);
    const h = Math.round(bmp.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d')!.drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    const out = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', JPEG_QUALITY));
    return out && out.size < file.size ? out : file;
  } catch {
    return file;
  }
}

/** Shrink (photos only — PDFs go as they are), store locally under its final storage path, and return the path. */
export async function addPhoto(file: Blob, companyId: string, userId: string, kind: string, bucket = 'photos'): Promise<string> {
  const pdf = file.type === 'application/pdf';
  const blob = pdf ? file : await shrinkImage(file);
  const path = storagePath(companyId, userId, kind, newId(), pdf ? 'pdf' : 'jpg');
  const row: PendingUpload = { path, bucket, blob, content_type: blob.type || (pdf ? 'application/pdf' : 'image/jpeg'), created_at: Date.now(), attempts: 0, last_error: null };
  await getDb().pending_uploads.put(row);
  void uploadPending();
  return path;
}

/** Store a file as it is (no resizing — e.g. a PNG signature) and queue it like a photo. */
export async function addFile(blob: Blob, companyId: string, userId: string, kind: string, bucket: string, ext: string): Promise<string> {
  const path = storagePath(companyId, userId, kind, newId(), ext);
  const row: PendingUpload = { path, bucket, blob, content_type: blob.type || 'application/octet-stream', created_at: Date.now(), attempts: 0, last_error: null };
  await getDb().pending_uploads.put(row);
  void uploadPending();
  return path;
}

/** Remove a photo that was never sent (e.g. removed from a draft). */
export async function dropPending(path: string): Promise<void> {
  await getDb().pending_uploads.delete(path);
}

let uploading: Promise<void> | null = null;
/** Send every waiting file. Safe to call often; one run at a time. */
export function uploadPending(): Promise<void> {
  if (!supabase) return Promise.resolve();  // local mode: files stay on the device
  if (uploading) return uploading;
  uploading = (async () => {
    const db = getDb();
    const items = await db.pending_uploads.orderBy('created_at').toArray();
    for (const it of items) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) break;
      const { error } = await supabase!.storage.from(it.bucket).upload(it.path, it.blob, { contentType: it.content_type, upsert: true });
      if (!error) {
        await db.pending_uploads.delete(it.path);
      } else {
        await db.pending_uploads.update(it.path, { attempts: it.attempts + 1, last_error: error.message });
        if (/fetch|network|Failed/i.test(error.message)) break;  // offline: try later
      }
    }
  })().finally(() => {
    uploading = null;
  });
  return uploading;
}

/** How many files are still waiting on this device. */
export function usePendingUploadCount(): number {
  return useLiveQuery(() => getDb().pending_uploads.count(), [], 0);
}

const signed = new Map<string, { url: string; until: number }>();
/** A displayable URL: the local copy while it's waiting, otherwise a short-lived signed link. */
export function usePhotoUrl(path: string, bucket = 'photos'): string | null {
  const pending = useLiveQuery(() => getDb().pending_uploads.get(path), [path]);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (pending) {
      const u = URL.createObjectURL(pending.blob);
      setUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    if (pending === undefined && supabase) {
      const hit = signed.get(path);
      if (hit && hit.until > Date.now()) { setUrl(hit.url); return; }
      let alive = true;
      void supabase.storage.from(bucket).createSignedUrl(path, 3600).then(({ data }) => {
        if (!alive || !data) return;
        signed.set(path, { url: data.signedUrl, until: Date.now() + 50 * 60_000 });
        setUrl(data.signedUrl);
      });
      return () => { alive = false; };
    }
    return undefined;
  }, [pending, path, bucket]);
  return url;
}

/** A stored file's bytes: the copy still waiting to upload on this device, else a fresh signed download. Null when unavailable. */
export async function fileBytes(path: string, bucket: string): Promise<Uint8Array | null> {
  const pending = await getDb().pending_uploads.get(path);
  if (pending) return new Uint8Array(await pending.blob.arrayBuffer());
  if (!supabase) return null;
  const { data } = await supabase.storage.from(bucket).createSignedUrl(path, 300);
  if (!data) return null;
  const res = await fetch(data.signedUrl);
  return res.ok ? new Uint8Array(await res.arrayBuffer()) : null;
}
